/**
 * Monitor Runtime — 实时监控 & 重规划
 *
 * 实时行程监控、异常检测、自动/手动重规划
 * 风格: 暗夜蓝 + 萤火暖金
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Tag, message } from 'antd';
import { monitorAPI } from '../api/monitor';

const TRIP_ID = 'trip_sh_001';

export default function MonitorRuntime() {
  const [timeline, setTimeline] = useState(null);
  const [environment, setEnvironment] = useState(null);
  const [replanning, setReplanning] = useState(false);
  const [replanResult, setReplanResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      let timelineData, envData;
      try {
        timelineData = await monitorAPI.getTimeline(TRIP_ID);
      } catch {
        timelineData = await monitorAPI.mockGetTimeline();
      }
      try {
        envData = await monitorAPI.getEnvironment(TRIP_ID);
      } catch {
        envData = await monitorAPI.mockGetEnvironment();
      }
      setTimeline(timelineData);
      setEnvironment(envData);
    } catch {
      message.error('加载监控数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReplan = useCallback(async () => {
    setReplanning(true);
    setReplanResult(null);
    try {
      let result;
      try {
        result = await monitorAPI.replan(TRIP_ID);
      } catch {
        result = await monitorAPI.mockReplan();
      }
      setReplanResult(result);
      message.success('重规划完成');
    } catch {
      message.error('重规划失败');
    } finally {
      setReplanning(false);
    }
  }, []);

  const getEventStatusConfig = (status) => {
    switch (status) {
      case 'done': return { color: '#34d399', bg: 'rgba(52,211,153,0.1)', icon: '✓' };
      case 'active': return { color: '#E8D28A', bg: 'rgba(232,210,138,0.1)', icon: '●' };
      case 'alert': return { color: '#f87171', bg: 'rgba(248,113,113,0.1)', icon: '⚠' };
      case 'upcoming': return { color: '#A8C7D8', bg: 'rgba(168,199,216,0.06)', icon: '○' };
      default: return { color: '#A8C7D8', bg: 'rgba(168,199,216,0.06)', icon: '○' };
    }
  };

  return (
    <div style={styles.pageShell}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseSoft {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
      `}</style>

      {/* ── Header ── */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <span style={{ fontSize: 28, marginRight: 12 }}>📡</span>
            Monitor Runtime
          </h1>
          <p style={styles.subtitle}>
            {timeline?.tripTitle || '实时行程监控 · 异常检测 · 自动重规划'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {timeline?.activeAnomalies > 0 && (
            <div style={styles.anomalyBadge}>
              <span style={{ fontSize: 14 }}>⚠️</span>
              <span style={{ fontSize: 13, color: '#f87171', fontWeight: 500 }}>
                {timeline.activeAnomalies} 异常
              </span>
            </div>
          )}
          <Button
            type="primary"
            size="large"
            onClick={handleReplan}
            loading={replanning}
            style={styles.replanBtn}
          >
            {replanning ? '重规划中…' : replanResult ? '已重规划' : '重新规划'}
            {!replanning && ' ↻'}
          </Button>
        </div>
      </header>

      <div style={styles.grid}>
        {/* ── Left: Timeline ── */}
        <div style={styles.leftCol}>
          <div style={styles.timelineCard}>
            <div style={styles.cardHeader}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                实时时间线
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', animation: 'pulseSoft 1.5s ease-in-out infinite' }} />
                <span style={{ fontSize: 12, color: '#34d399' }}>Live</span>
              </div>
            </div>

            <div style={styles.timelineList}>
              {timeline?.events?.map((evt, idx) => {
                const conf = getEventStatusConfig(evt.status);
                return (
                  <div key={idx} style={styles.timelineItem}>
                    {/* 时间线 */}
                    <div style={styles.timelineLine}>
                      <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: conf.color,
                        boxShadow: evt.status === 'alert' ? `0 0 12px ${conf.color}` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 8,
                        color: 'var(--night-950)',
                      }}>
                        {evt.status === 'done' ? '✓' : ''}
                      </div>
                      {idx < timeline.events.length - 1 && (
                        <div style={{
                          width: 2,
                          flex: 1,
                          minHeight: 16,
                          background: evt.status === 'done' ? 'rgba(52,211,153,0.2)' : 'var(--border-subtle)',
                          margin: '2px auto',
                        }} />
                      )}
                    </div>

                    {/* 内容 */}
                    <div style={{ flex: 1, paddingBottom: idx < timeline.events.length - 1 ? 12 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={styles.eventTime}>{evt.time}</span>
                        <span style={{
                          fontSize: 14,
                          fontWeight: evt.status === 'alert' ? 600 : 500,
                          color: evt.status === 'alert' ? '#f87171' : 'var(--text-primary)',
                        }}>
                          {evt.title}
                        </span>
                        {evt.status === 'done' && <span style={{ fontSize: 12, color: '#34d399' }}>✓</span>}
                        {evt.status === 'alert' && <span style={{ fontSize: 12, color: '#f87171' }}>⚠</span>}
                      </div>
                      {evt.alertReason && (
                        <div style={styles.alertReason}>
                          <span style={{ fontSize: 12, marginRight: 6 }}>⚠</span>
                          <span style={{ fontSize: 12, color: '#f87171' }}>{evt.alertReason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Anomaly Alert Card */}
          {timeline?.activeAnomalies > 0 && (
            <div style={styles.alertCard}>
              <div style={styles.alertHeader}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#f87171', margin: 0 }}>
                    豫园临时闭馆
                  </h4>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    13:45 宣布因天气影响临时闭馆。原路线受影响: +1.7h 延误
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                <span style={styles.alertTag}>🌧️ 大雨预警</span>
                <span style={styles.alertTag}>🛣️ 路线受影</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Environment + Replan ── */}
        <div style={styles.rightCol}>
          {/* Environment */}
          <div style={styles.envCard}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
              🌤️ 环境数据
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={styles.envItem}>
                <span style={{ fontSize: 16 }}>🌧️</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>天气</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {environment?.weather?.condition} · {environment?.weather?.temperature}
                  </span>
                </div>
                <span style={{ fontSize: 11, color: '#f87171', background: 'rgba(248,113,113,0.1)', padding: '2px 8px', borderRadius: 8 }}>
                  {environment?.weather?.alert}
                </span>
              </div>
              <div style={styles.envItem}>
                <span style={{ fontSize: 16 }}>📍</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>当前位置</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {environment?.location?.current} · 距下一站 {environment?.location?.distanceToNext}
                  </span>
                </div>
                <span style={{ fontSize: 11, color: '#34d399' }}>ETA {environment?.location?.etaNext}</span>
              </div>
              <div style={styles.envItem}>
                <span style={{ fontSize: 16 }}>🚇</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>交通</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {environment?.traffic?.overall} · 地铁{environment?.traffic?.metro}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Replan Result */}
          <div style={{
            ...styles.replanCard,
            borderColor: replanResult ? 'rgba(52,211,153,0.2)' : 'var(--border-default)',
            background: replanResult ? 'rgba(52,211,153,0.03)' : 'var(--surface-1)',
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
              {replanResult ? '✅ 新路线已生成' : '🔄 重规划结果'}
            </h3>
            {replanResult ? (
              <div>
                {replanResult.newPlan?.map((item, i) => (
                  <div key={i} style={styles.replanItem}>
                    <span style={styles.replanTime}>{item.time}</span>
                    <span style={{
                      fontSize: 13,
                      color: item.status === 'new' ? '#34d399' : 'var(--text-primary)',
                      fontWeight: item.status === 'new' ? 600 : 400,
                    }}>
                      {item.title}
                    </span>
                    {item.status === 'new' && <span style={styles.newBadge}>NEW</span>}
                  </div>
                ))}
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
                    {replanResult.summary}
                  </p>
                  <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      恢复耗时: {replanResult.recoveryTime}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      置信度: {Math.round(replanResult.confidence * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={styles.replanEmpty}>
                <span style={{ fontSize: 24 }}>🚀</span>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
                  检测到异常后可触发重规划
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  pageShell: {
    minHeight: '100vh',
    /* 透出 #root::before 的森林背景（登录页原图） */
    background: 'transparent',
    padding: '32px 36px 80px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-primary)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    animation: 'fadeUp var(--duration-slow) var(--ease) both',
  },
  title: {
    fontSize: 26,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginTop: 6,
  },
  anomalyBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 16px',
    borderRadius: 'var(--radius-pill)',
    background: 'rgba(248,113,113,0.06)',
    border: '1px solid rgba(248,113,113,0.2)',
  },
  replanBtn: {
    background: 'linear-gradient(135deg, var(--accent) 0%, #C4A96A 100%)',
    border: 'none',
    borderRadius: 10,
    height: 40,
    fontWeight: 600,
    color: 'var(--night-950)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 360px',
    gap: 20,
  },
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  timelineCard: {
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    padding: '24px',
    animation: 'fadeUp var(--duration-slow) var(--ease) 100ms both',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  timelineList: {
    display: 'flex',
    flexDirection: 'column',
  },
  timelineItem: {
    display: 'flex',
    gap: 14,
  },
  timelineLine: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 12,
  },
  eventTime: {
    fontSize: 12,
    color: 'var(--accent)',
    fontFamily: 'var(--font-mono)',
    fontWeight: 600,
  },
  alertReason: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 4,
    padding: '6px 10px',
    background: 'rgba(248,113,113,0.06)',
    borderRadius: 8,
    border: '1px solid rgba(248,113,113,0.15)',
  },
  alertCard: {
    padding: '20px 24px',
    background: 'rgba(248,113,113,0.04)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(248,113,113,0.2)',
    animation: 'fadeUp var(--duration-slow) var(--ease) 200ms both',
  },
  alertHeader: {
    display: 'flex',
    gap: 14,
  },
  alertTag: {
    fontSize: 11,
    padding: '4px 10px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--glass-sm)',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-secondary)',
  },
  envCard: {
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    padding: '20px',
    animation: 'fadeUp var(--duration-slow) var(--ease) 200ms both',
  },
  envItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  replanCard: {
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    padding: '20px',
    animation: 'fadeUp var(--duration-slow) var(--ease) 300ms both',
  },
  replanItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
  },
  replanTime: {
    fontSize: 12,
    color: '#34d399',
    fontFamily: 'var(--font-mono)',
    fontWeight: 600,
  },
  newBadge: {
    fontSize: 9,
    padding: '2px 6px',
    borderRadius: 4,
    background: 'rgba(52,211,153,0.1)',
    color: '#34d399',
    fontWeight: 600,
  },
  replanEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 0',
    textAlign: 'center',
  },
};
